import { Controller } from "@hotwired/stimulus";
import { toastManager } from "../components/DownloadTray/ToastManager";
import { regionFilterFn } from "./venue_region_filter";

// GR-3121: state -> city -> property drill-down on the sourcing wizard step.
// All options are server-rendered; this controller only narrows what is
// offered, so with JS disabled every property remains selectable.
// Non-matching options are removed outright (native select popups render
// hidden/disabled options greyed-out rather than gone) and restored from
// the full list stashed at connect.
//
// GR-3372: the quote form reuses it over a grouped property select — the
// request's preferred properties first, the rest of the catalog under them.
// So the stash is of top-level nodes rather than options, groups are rebuilt
// with only their matching options and dropped once empty, and an option
// marked data-pinned is kept whatever the filters say. Pinning is what keeps
// the request's own shortlist, and the hotel already on the quote, on the
// list while somebody browses for something else.
//
// GR-3376: the wizard's step 3 browses the whole venue directory, which is
// five figures of venues — too many to server-render and filter. Given an
// options URL the controller switches from narrowing a static list to asking
// the server for the cities and properties under the current selection. The
// quote form has no such URL and keeps the client-side filtering. Selecting a
// property also moves the map: the picker dispatches the selection and the
// browsed area, and GR-3377's cluster map listens for it on the document.
// GR-3392: the directory browses meeting-grade venues by default, so the step
// carries a link to the rest of it. The link works on its own with the JS off;
// with the controller up the click becomes another fetch, so the browse someone
// is in the middle of survives pressing it.
export default class SourcingPropertyPickerController extends Controller {
  static targets = [
    "state", "city", "property", "hint", "mapCaption", "scope", "scopeHint", "scopeToggle",
    // GR-3396: the panels and the native fields they stand in front of.
    "nativeField", "panelField", "statePanel", "cityPanel", "propertyPanel",
    // GR-3410: the explicit clear beside the property control.
    "clearPropertyButton",
    // GR-4249: the city and state the property picker is browsing.
    "locationHeader",
  ];
  static values = { optionsUrl: String, allVenues: Boolean };

  connect() {
    this.allNodes = new Map();
    [this.cityTarget, this.propertyTarget].forEach((select) => {
      this.allNodes.set(
        select,
        Array.from(select.children, (node) => node.cloneNode(true))
      );
    });
    // In directory mode the server already rendered the scoped lists, so
    // there is nothing to narrow and nothing to fetch until something changes.
    if (!this.browsesDirectory) this.applyFilters();
    this.enhanceWithPanels();
  }

  // === GR-3396: panels in front of the selects ===

  // The selects are the value and the panels are the chooser. Swapping them
  // here rather than in the markup is what keeps the no-JS path honest: a
  // browser that never reaches this line keeps the native selects it was
  // served, with every option still on them.
  enhanceWithPanels() {
    if (!this.hasPanelFieldTarget) return;

    this.nativeFieldTargets.forEach((field) => (field.hidden = true));
    this.panelFieldTargets.forEach((field) => (field.hidden = false));

    if (this.hasStatePanelTarget) this.statePanelTarget.filterFn = regionFilterFn;
  }

  statePicked(event) {
    this.applyPick(this.stateTarget, event);
  }

  cityPicked(event) {
    this.applyPick(this.cityTarget, event);
  }

  propertyPicked(event) {
    this.applyPick(this.propertyTarget, event);
  }

  // Mirror the panel's choice onto the select it stands in front of, then let
  // the select's own change event carry it into everything downstream: the
  // refetch, the focus dispatch, the map. Nothing below this line knows a
  // panel exists.
  applyPick(select, event) {
    const value = event.detail?.value ?? "";
    // GR-3477: Primer re-emits itemActivated for an item that is already
    // checked, so pressing the current pick again arrives here as a pick. The
    // change event below is the cascade, and a cascade off an unchanged state
    // would clear the city and the property the reader had browsed to.
    if (select.value === value) return;

    if (!this.hasOption(select, value)) this.addOption(select, value, event.detail?.item);

    select.value = value;
    if (select.selectedIndex === -1) select.value = "";
    select.dispatchEvent(new Event("change", { bubbles: true }));
  }

  hasOption(select, value) {
    return Array.from(select.options).some((option) => option.value === value);
  }

  // A remote panel can offer a venue the select has never been sent — typing a
  // name searches the whole state, while the select holds the current city's
  // hundred. Add what was chosen so the value still submits and the map still
  // has coordinates to read.
  addOption(select, value, item) {
    if (!value) return;

    const content = item?.querySelector("[data-value]") || item;
    const option = this.buildOption({
      value,
      label: (item?.textContent || value).trim(),
      lat: content?.dataset?.lat,
      lng: content?.dataset?.lng,
      locationId: content?.dataset?.locationId,
      region: content?.dataset?.region,
      venueId: content?.dataset?.venueId,
    });
    select.appendChild(option);
  }

  // The panels fetch their own items, so they need the browse in their src the
  // same way the picker carries it in its own fetch. The flag is read before
  // the target rather than alongside it: Stimulus's target getters throw on a
  // missing target instead of answering null, so a panel handed over as an
  // argument is a panel already demanded.
  syncPanelSources() {
    if (this.hasCityPanelTarget) this.rescope(this.cityPanelTarget, { city: false });
    if (this.hasPropertyPanelTarget) this.rescope(this.propertyPanelTarget, { city: true });
  }

  rescope(panel, { city }) {
    const input = panel.querySelector("remote-input");
    const holder = input || panel;
    const attribute = input ? "src" : "data-src";
    const raw = holder.getAttribute(attribute);
    if (!raw) return;

    const url = new URL(raw, window.location.origin);
    this.carry(url, "sourcing_state", this.stateTarget.value);
    if (city) this.carry(url, "sourcing_location_id", this.cityTarget.value);
    if (this.allVenuesValue) url.searchParams.set("sourcing_all_venues", "1");
    else url.searchParams.delete("sourcing_all_venues");
    holder.setAttribute(attribute, url.toString());
  }

  get browsesDirectory() {
    return this.hasOptionsUrlValue && this.optionsUrlValue !== "";
  }

  // GR-3404: the controls cascade. A city belongs to the state above it and a
  // property to the city above that, so a broader change takes the narrower
  // picks with it: leaving them would scope the reload by a city in another
  // state, or submit a property nobody browsed to.
  // The cascade is the wizard's drill-down only. The quote form reuses this
  // controller over a static list where GR-3372 promises the opposite: the
  // hotel already on the quote, and the request's shortlist, survive any
  // narrowing. Clearing them there would throw away the answer someone had
  // already given.
  stateChanged() {
    if (!this.browsesDirectory) return this.applyFilters();

    this.clearCity();
    this.clearProperty();
    this.updateLocationHeader();
    return this.reload();
  }

  cityChanged() {
    if (!this.browsesDirectory) return this.applyFilters();

    this.clearProperty();
    this.updateLocationHeader();
    return this.reload();
  }

  // GR-4249: the header over the property picker says where the hotels are,
  // in the same "City, State" the server renders it as.
  updateLocationHeader() {
    if (!this.hasLocationHeaderTarget) return;

    const header = this.locationHeaderTarget;
    const { region, city } = this.browsedArea();
    const state = region && (this.regionNames[region] || region);
    const name = [city, state].filter(Boolean).join(", ");
    header.textContent = name || header.dataset.blankLabel || "";
  }

  get regionNames() {
    try {
      return JSON.parse(this.locationHeaderTarget.dataset.regionNames || "{}");
    } catch {
      return {};
    }
  }

  // GR-3404: adding a preference empties the property control so the next one
  // can be picked straight away, and leaves the browse where the requester is
  // working. Clearing the control is not clearing the preference: the shortlist
  // below is the record of what was added, and it stays pinned and selectable.
  preferenceSubmitted(event) {
    if (!event.detail?.success) return;
    if (event.detail.formSubmission?.submitter?.name !== "add_preference") return;

    this.clearProperty();
    this.focusMap();
  }

  // GR-3410: the explicit clear. Until this existed a selection only left the
  // control by cascade, so there was no way to put one back and look at the
  // city again. Same clear the cascades run, then the same coordinate-less
  // focus, so the map re-frames the browsed area (!4318) rather than staying
  // zoomed on a venue nobody has selected any more.
  //
  // The affordance is a real link carrying a blank property, so it works with
  // the JS off; with the controller up the click is handled here and costs no
  // page load.
  clearPropertySelection(event) {
    event?.preventDefault();
    this.clearProperty();
    this.focusMap();
  }

  clearCity() {
    this.clear(this.cityTarget, this.hasCityPanelTarget ? this.cityPanelTarget : null);
  }

  clearProperty() {
    this.clear(this.propertyTarget, this.hasPropertyPanelTarget ? this.propertyPanelTarget : null);
  }

  // Empties the control without firing change: the caller is already on its way
  // to a reload, and a second pass round the cascade would only undo itself.
  clear(select, panel) {
    if (select) select.value = "";
    if (!panel) return;

    // The panel is what the eye reads, so a select emptied behind a button
    // still printing the old pick has not been cleared as far as anyone can
    // tell. Both of these are public on SelectPanelElement.
    const marker = panel.ariaSelectionType || "aria-selected";
    panel.querySelectorAll(`[${marker}="true"]`).forEach((node) => node.setAttribute(marker, "false"));

    const label = panel.invokerLabel || panel.querySelector(".Button-label");
    if (label && panel.dataset.blankLabel) label.textContent = panel.dataset.blankLabel;

    // The label only a screen reader reads. `dynamic_aria_label_prefix` has
    // Primer stamp the pick onto the show button's aria-label, and it rewrites
    // that attribute nowhere but the next activation — so a control cleared
    // here would go on announcing a venue it no longer holds. Removing it
    // rather than blanking it hands the button back to its own text, which is
    // the blank label just written above; Primer renders no aria-label of its
    // own until something is picked.
    const invoker = panel.invokerElement || label?.closest("button");
    invoker?.removeAttribute("aria-label");
  }

  propertyChanged() {
    this.focusMap();
  }

  // The link's own href is the no-JS answer and stays correct either way; here
  // it is a re-fetch of the browse already on screen.
  toggleScope(event) {
    if (!this.browsesDirectory) return;

    event?.preventDefault();
    this.allVenuesValue = !this.allVenuesValue;
    this.updateScope();
    return this.reload();
  }

  // === directory mode ===

  async reload() {
    const url = new URL(this.optionsUrlValue, window.location.origin);
    url.searchParams.set("sourcing_state", this.stateTarget.value);
    url.searchParams.set("sourcing_location_id", this.cityTarget.value);
    if (this.allVenuesValue) url.searchParams.set("sourcing_all_venues", "1");

    // Browsing outruns the network, so only the newest answer may land.
    const token = (this.requestToken = (this.requestToken || 0) + 1);
    let payload;
    // GR-3477: a refused response was already handled here; a connection that
    // drops is an exception, and it used to escape as an unhandled rejection
    // and leave the browse half-done. The selects keep the options they have,
    // which are the ones the reader can still see, and the toast says the
    // list did not move.
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(`sourcing options: ${response.status}`);

      payload = await response.json();
    } catch (error) {
      if (token !== this.requestToken) return;

      console.error("Sourcing property options failed to load:", error);
      toastManager.show({
        message: "Could not load venues for this area",
        subtitle: "The list below is the one you were already browsing.",
        variant: "danger",
        icon: "alert",
        autoCloseMs: 5000,
      });
      return;
    }
    if (token !== this.requestToken) return;

    this.replaceOptions(this.cityTarget, [{ options: payload.cities }]);
    this.replaceOptions(this.propertyTarget, payload.properties);
    if (this.hasHintTarget) {
      this.hintTarget.textContent = payload.hint || "";
      this.hintTarget.hidden = !payload.hint;
    }
    // The server is the authority on which half it just answered with: asking
    // for everything in a state that has nothing held back is still an answer
    // about everything.
    if (payload.allVenues !== undefined) this.allVenuesValue = Boolean(payload.allVenues);
    this.updateScope(payload.scopeHint);
    this.syncPanelSources();
    this.focusMap();
  }

  // Says what is on show and offers the other half. The link keeps carrying the
  // browse, so following it without JS lands where the picker already is.
  updateScope(hint) {
    if (this.hasScopeHintTarget && hint !== undefined) this.scopeHintTarget.textContent = hint || "";
    if (this.hasScopeTarget) {
      const text = this.hasScopeHintTarget ? this.scopeHintTarget.textContent : "";
      this.scopeTarget.hidden = text === "";
    }
    if (!this.hasScopeToggleTarget) return;

    const link = this.scopeToggleTarget;
    link.textContent = this.allVenuesValue ? link.dataset.allLabel : link.dataset.gradedLabel;

    const url = new URL(link.href, window.location.origin);
    this.carry(url, "sourcing_state", this.stateTarget.value);
    this.carry(url, "sourcing_location_id", this.cityTarget.value);
    if (this.allVenuesValue) url.searchParams.delete("sourcing_all_venues");
    else url.searchParams.set("sourcing_all_venues", "1");
    link.href = url.toString();
  }

  carry(url, key, value) {
    if (value) url.searchParams.set(key, value);
    else url.searchParams.delete(key);
  }

  // Rebuilds a select from the server's answer, keeping its blank prompt (and
  // that prompt's wording) at the top. A selection the new list does not offer
  // falls back to blank.
  replaceOptions(select, groups) {
    const selectedValue = select.value;
    const blank = select.querySelector('option[value=""]');
    const nodes = blank ? [blank] : [];

    (groups || []).forEach((group) => {
      const options = (group.options || []).map((data) => this.buildOption(data));
      if (options.length === 0) return;
      if (!group.label) {
        nodes.push(...options);
        return;
      }

      const optgroup = document.createElement("optgroup");
      optgroup.label = group.label;
      optgroup.replaceChildren(...options);
      nodes.push(optgroup);
    });

    select.replaceChildren(...nodes);
    select.value = selectedValue;
    if (select.selectedIndex === -1) select.value = "";
  }

  buildOption({ value, label, region, locationId, latitude, longitude, lat, lng, pinned, venueId }) {
    latitude = latitude ?? lat;
    longitude = longitude ?? lng;
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    if (region) option.dataset.region = region;
    if (locationId) option.dataset.locationId = locationId;
    if (latitude) option.dataset.lat = latitude;
    if (longitude) option.dataset.lng = longitude;
    // GR-3433: the directory venue the map's popup asks about once this option
    // is the one in focus. A catalog property that was never in the directory
    // carries none, and the popup shows what it was served.
    if (venueId) option.dataset.venueId = venueId;
    if (pinned) option.dataset.pinned = "true";
    return option;
  }

  // === the map following the selection ===

  // The map should show what is selected, not what was last saved. Everything
  // it needs is on the option, and the browsed area travels with it so the map
  // can re-scope its markers even before a property is picked. GR-3377's
  // cluster map listens for sourcing-property-picker:focus on the document;
  // the event bubbles from here, so a @document or a @window action filter
  // both catch it. Nothing here reaches into the map's markup.
  focusMap() {
    const option = this.propertyTarget.selectedOptions[0];
    const latitude = option && option.dataset.lat;
    const longitude = option && option.dataset.lng;
    const point =
      latitude && longitude
        ? {
            latitude: Number(latitude),
            longitude: Number(longitude),
            name: option.textContent.trim(),
            // GR-3433: the map's focused marker reads the venue's facts off
            // the venue endpoint, and the option already knows which venue it
            // is. Without this the map would have to guess it out of a value
            // that names a catalog property half the time.
            venueId: option.dataset.venueId || null,
          }
        : null;

    this.dispatch("focus", { detail: { ...this.browsedArea(), ...point } });
    this.updateMapCaption(point);
    // GR-3410: every moment that re-aims the map is a moment the selection
    // changed, so the clear affordance follows it from here rather than being
    // remembered separately at each of those call sites.
    this.syncClearAffordance();
  }

  // Nothing selected, nothing to clear: the affordance does not stand there
  // offering to undo something that has not happened. A selection with no
  // coordinates is still a selection, so this asks the select rather than the
  // map point.
  syncClearAffordance() {
    if (!this.hasClearPropertyButtonTarget) return;

    this.clearPropertyButtonTarget.hidden = this.propertyTarget.value === "";
  }

  // The caption labels the map box rather than anything inside it, so it is
  // the picker's to keep current even though the map's own markup is not.
  updateMapCaption(point) {
    if (this.hasMapCaptionTarget) this.mapCaptionTarget.textContent = point ? point.name : "";
  }

  browsedArea() {
    const city = this.cityTarget.selectedOptions[0];
    return {
      region: this.stateTarget.value || null,
      city: city && city.value ? city.textContent.trim() : null,
      // GR-3392: the map draws what the list offers, so the scope travels with
      // the area rather than being worked out twice.
      allVenues: Boolean(this.allVenuesValue),
    };
  }

  // === client-side filtering (the quote form) ===

  applyFilters() {
    const state = this.stateTarget.value;

    this.filterOptions(this.cityTarget, (option) => !state || option.dataset.region === state);

    const city = this.cityTarget.value;
    this.filterOptions(this.propertyTarget, (option) => {
      if (option.dataset.pinned) return true;
      if (city) return option.dataset.locationId === city;
      return !state || option.dataset.region === state;
    });
  }

  // Rebuild the select from the stashed full list, keeping only the blank
  // prompt and matching options. A selection that no longer matches falls
  // back to blank and fires change so downstream selects re-filter.
  filterOptions(select, matches) {
    const selectedValue = select.value;
    const kept = this.allNodes.get(select).flatMap((node) => this.keptNode(node, matches));
    select.replaceChildren(...kept);

    select.value = selectedValue;
    if (select.selectedIndex === -1) select.value = "";
    if (selectedValue !== "" && select.value === "") {
      select.dispatchEvent(new Event("change"));
    }
  }

  // Clones, because the stash is the only copy of the full list: a group
  // rebuilt in place would lose the options this pass filtered out.
  keptNode(node, matches) {
    if (node.tagName !== "OPTGROUP") {
      return node.value === "" || matches(node) ? [node.cloneNode(true)] : [];
    }

    const options = Array.from(node.children).filter((option) => matches(option));
    if (options.length === 0) return [];

    const group = node.cloneNode(false);
    group.replaceChildren(...options.map((option) => option.cloneNode(true)));
    return [group];
  }
}
